import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { IMG } from "@/lib/content";
import { goToEstimate, scrollToHash } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Custom Countertops.", "Fabricated for", "Your Home."];

const MaskLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${i === 2 ? "ks-gold-text italic" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#0B0F17]">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <motion.img
          src={IMG.kitchen}
          alt="Custom quartz kitchen island and countertops fabricated and installed by KS Granite House"
          className="h-full w-full object-cover object-[50%_62%]"
          initial={{ clipPath: "inset(12% 12% 12% 12%)", filter: "brightness(0.4)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", filter: "brightness(1)" }}
          transition={{ duration: 1.6, ease: EASE }}
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17]/95 via-[#0B0F17]/70 to-[#0B0F17]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-[#0B0F17]/60" />
      <div className="ks-grain absolute inset-0" aria-hidden="true" />

      <motion.div style={{ y: textY }} className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-16 pt-32 sm:px-8 lg:pb-24">
        <motion.p
          className="ks-eyebrow text-[#CDA85B]"
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="ks-eyebrow-line" aria-hidden="true" />
          Kansas City · Kansas & Missouri
        </motion.p>

        <h1 data-testid="hero-heading" className="mt-6 max-w-4xl font-display text-[2.5rem] leading-[0.98] min-[400px]:text-[2.8rem] tracking-[-0.01em] text-[#F5F0E6] sm:text-6xl lg:text-[6.2rem]">
          {LINES.map((l, i) => <MaskLine key={l} text={l} i={i} />)}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1 }}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-[#D6D3CC] sm:text-lg">
            Granite, quartz, quartzite and marble countertops, laser-templated, fabricated in our own shop and professionally installed by a team with 10+ years of experience.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Materials">
            {["Granite", "Quartz", "Quartzite", "Marble"].map((m) => (
              <li key={m} className="rounded-full border border-[#CDA85B]/35 bg-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[#ECE6DA] backdrop-blur-md">{m}</li>
            ))}
          </ul>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button onClick={() => goToEstimate()} data-testid="hero-estimate-btn" className="ks-btn-gold group justify-center">
              Get Your Free Estimate <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToHash("#work")} data-testid="hero-work-btn" className="ks-btn-ghost justify-center">
              View Our Work
            </button>
          </div>
        </motion.div>

        <motion.div
          data-testid="hero-turnaround-badge"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 1.3, ease: EASE }}
          className="mt-10 flex max-w-sm items-center gap-4 rounded-2xl border border-white/10 bg-[#0B0F17]/60 p-4 backdrop-blur-xl lg:absolute lg:bottom-24 lg:right-8 lg:mt-0"
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#CDA85B]/50 text-[#CDA85B]"><Clock className="h-5 w-5" /></span>
          <span>
            <span className="block font-display text-2xl text-[#F5F0E6]">5–10 business days</span>
            <span className="block text-xs uppercase tracking-[0.16em] text-[#B8B4AA]">Typical installation after deposit</span>
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
